import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { KeyRound, Users } from 'lucide-react';
import { Avatar, Badge, Button, Card, CardContent, CardHeader, CardTitle, Skeleton } from '@mlm/ui';
import { getMyLeads } from '../lib/api';
import { fmtDate, initialsFromName } from '../lib/format';
import { KpiCard } from '../components/kpi-card';
import { EmptyState } from '../components/empty-state';
import { ActivateLeadDialog, type ActivateLeadTarget } from '../components/activate-lead-dialog';

export function LeadsPage() {
  const q = useQuery({ queryKey: ['my-leads'], queryFn: getMyLeads });
  const all = q.data ?? [];
  const warm = all.filter((l) => !l.hasActiveSlot);
  const active = all.filter((l) => l.hasActiveSlot);
  const [activateTarget, setActivateTarget] = useState<ActivateLeadTarget | null>(null);

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-h1 flex items-center gap-2">
            <Users className="h-6 w-6 text-muted-foreground" /> Leads
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            People who signed up through your replicated link. Warm leads haven't activated a slot
            yet.
          </p>
        </div>
        <Button asChild variant="outline" size="sm">
          <Link to="/invite">Share link</Link>
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <KpiCard label="Active" value={String(active.length)} />
        <KpiCard label="Warm leads" value={String(warm.length)} />
      </div>

      {q.isLoading ? (
        <Card>
          <CardContent className="p-6">
            <Skeleton className="h-24" />
          </CardContent>
        </Card>
      ) : all.length === 0 ? (
        <EmptyState
          icon={Users}
          title="No one has signed up under you yet"
          description="Share your replicated link and the people who join through it will show up here."
          action={
            <Button asChild size="sm">
              <Link to="/invite">Share your link</Link>
            </Button>
          }
        />
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">All sponsored</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <ul className="divide-y">
              {all.map((l) => (
                // Stacks on phones (identity line, then the button on its own
                // row) and becomes one row from `sm` up. The name column is
                // the only shrinkable item, so a nowrap button beside it on a
                // 360px screen used to squeeze the name to an ellipsis.
                <li
                  key={l.id}
                  className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:px-6"
                >
                  <div className="flex min-w-0 flex-1 items-center gap-3">
                    <Avatar
                      size="md"
                      initials={initialsFromName(l.fullName ?? l.email, '??')}
                      className="bg-primary/10 text-primary"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-medium">{l.fullName ?? l.email}</div>
                      <div className="truncate text-xs text-muted-foreground">
                        {l.email} · joined {fmtDate(l.signedUpAt)}
                      </div>
                      <div className="mt-0.5 truncate text-xs text-muted-foreground">
                        Registered under slot:{' '}
                        {l.slotCode ? (
                          <span className="font-medium text-foreground">{l.slotCode}</span>
                        ) : (
                          <span>—</span>
                        )}
                      </div>
                    </div>
                    <Badge className="shrink-0" variant={l.hasActiveSlot ? 'success' : 'warning'}>
                      {l.hasActiveSlot ? 'Active' : 'Warm'}
                    </Badge>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    className="w-full shrink-0 sm:w-auto"
                    onClick={() =>
                      setActivateTarget({
                        id: l.id,
                        name: l.fullName ?? l.email,
                        hasActiveSlot: l.hasActiveSlot,
                      })
                    }
                  >
                    <KeyRound className="h-3.5 w-3.5" />
                    {l.hasActiveSlot ? 'Add slot with code' : 'Activate with code'}
                  </Button>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      <ActivateLeadDialog lead={activateTarget} onClose={() => setActivateTarget(null)} />
    </div>
  );
}
